import type { FC } from 'react';
import React, { useEffect } from 'react';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { Checkbox } from '@motorway/mw-highway-code/alpha';
import { Textarea, toast } from '@motorway/mw-highway-code/pre-alpha';

import { triggerGradingModalSubmitFeedbackButtonClickedEvent } from 'Services/analytics/events';

import texts from './ExteriorNumericGradingModal.texts';

import styles from './ExteriorNumericGradingModal.module.scss';

const LocalT = new MDText(texts);

const FEEDBACK_REASONS = Object.keys(texts.footer.negativeFeedbackForm.checkboxes) as Array<
	keyof typeof texts.footer.negativeFeedbackForm.checkboxes
>;

const generateNegativeFeedbackSnowplowLabel = (feedbackData: {
	criteriaWrong: boolean;
	doesNotReflectCondition: boolean;
	freeText: string;
	notAccurate: boolean;
	somethingElse: boolean;
}) => {
	const reasons = FEEDBACK_REASONS.filter((reason) => feedbackData[reason]).map((reason) =>
		LocalT.translate(`footer.negativeFeedbackForm.checkboxes.${reason}`),
	);

	return JSON.stringify({
		isPositive: false,
		reasons,
		text: feedbackData.freeText,
	});
};

export const NegativeFeedbackForm: FC<{ onDissmissModal?: () => void }> = ({ onDissmissModal }) => {
	useEffect(() => {
		const modalBackdrop = document.querySelector('[aria-label="modal-backdrop"]') as HTMLElement;

		if (modalBackdrop?.scrollTo) {
			modalBackdrop.scrollTo({ behavior: 'smooth', top: modalBackdrop.scrollHeight });
		}
	}, []);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const formData = new FormData(event.target as HTMLFormElement);

		const feedbackData = {
			criteriaWrong: formData.has('criteriaWrong'),
			doesNotReflectCondition: formData.has('doesNotReflectCondition'),
			freeText: (formData.get('freeText') as string)?.trim()?.replaceAll('\n', ' ') || '',
			notAccurate: formData.has('notAccurate'),
			somethingElse: formData.has('somethingElse'),
		};

		const negativeFeedbackSnowplowLabel = generateNegativeFeedbackSnowplowLabel(feedbackData);

		triggerGradingModalSubmitFeedbackButtonClickedEvent(negativeFeedbackSnowplowLabel);

		toast({
			content: LocalT.translate('toast.content'),
			heading: LocalT.translate('toast.heading'),
			variant: 'success',
		});

		onDissmissModal?.();
	};

	return (
		<>
			<h5 className={styles.negativeFeedbackPrompt}>
				{LocalT.translate(texts.footer.negativeFeedbackForm.checkboxPrompt)}
			</h5>
			<form className={styles.negativeFeedbackForm} onSubmit={handleSubmit}>
				<div className={styles.negativeFeedbackInputContainer}>
					<div className={styles.negativeFeedbackCheckboxContainer}>
						<Checkbox
							formProps={{ input: { name: 'criteriaWrong' } }}
							id="criteriaWrong"
							label={LocalT.translate(texts.footer.negativeFeedbackForm.checkboxes.criteriaWrong)}
						/>
						<Checkbox
							formProps={{ input: { name: 'doesNotReflectCondition' } }}
							id="doesNotReflectCondition"
							label={LocalT.translate('footer.negativeFeedbackForm.checkboxes.doesNotReflectCondition')}
						/>
						<Checkbox
							formProps={{ input: { name: 'notAccurate' } }}
							id="notAccurate"
							label={LocalT.translate('footer.negativeFeedbackForm.checkboxes.notAccurate')}
						/>
						<Checkbox
							formProps={{ input: { name: 'somethingElse' } }}
							id="somethingElse"
							label={LocalT.translate('footer.negativeFeedbackForm.checkboxes.somethingElse')}
						/>
					</div>
					<div className={styles.negativeFeedbackFreeTextContainer}>
						<Textarea
							fullWidth
							formProps={{ input: { name: 'freeText' } }}
							id="freeText"
							label={'Free text field'}
							placeholder={`${LocalT.translate(texts.footer.negativeFeedbackForm.freeTextPlaceholder)}`}
						/>
					</div>
				</div>
				<Button label={LocalT.translate(texts.footer.negativeFeedbackForm.submit)} type="submit" />
			</form>
		</>
	);
};
